import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { ErrorScreenAction, ErrorScreenProps } from '../errorScreen.types';

type ErrorScreenContentProps = Pick<
  ErrorScreenProps,
  'title' | 'description' | 'illustration' | 'note' | 'actions'
>;

const CONTENT_MAX_WIDTH = 410;
const ILLUSTRATION_GAP = 5;
const ACTIONS_GAP = 2.5;
const TEXT_GAP = 1;
const BUTTONS_GAP = 2;

const ErrorScreenContent = ({
  title,
  description,
  illustration,
  note,
  actions,
}: ErrorScreenContentProps) => {
  const { translate } = useTranslation();

  const primaryAction = actions?.primary;
  const secondaryAction = actions?.secondary;

  const renderAction = (action: ErrorScreenAction, isPrimary: boolean) => (
    <Button
      variant="contained"
      color={isPrimary ? 'primary' : 'secondary'}
      size="large"
      disabled={action.disabled}
      loading={action.isLoading}
      href={action.href}
      to={action.to}
      testId={action.testId}
      startIcon={action.icon && <Icon icon={action.icon} size="sm" />}
      onClick={action.onClick}
    >
      {translate(action.label, action.labelValues)}
    </Button>
  );

  return (
    <Stack sx={{ alignItems: 'flex-start', gap: ILLUSTRATION_GAP, maxWidth: CONTENT_MAX_WIDTH }}>
      {illustration && <Box aria-hidden>{illustration}</Box>}

      <Stack sx={{ alignItems: 'flex-start', gap: ACTIONS_GAP }}>
        <Stack sx={{ alignItems: 'flex-start', gap: TEXT_GAP }}>
          <Typography component="h1" variant="h3" color="text.primary">
            {translate(title.text, title.values)}
          </Typography>

          {description && (
            <Typography variant="h6" color="text.secondary">
              {translate(description.text, description.values)}
            </Typography>
          )}

          {note && (
            <Typography component="p" variant="subtitle1" color="text.tertiary">
              ({note})
            </Typography>
          )}
        </Stack>

        {(primaryAction || secondaryAction) && (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: BUTTONS_GAP }}>
            {secondaryAction && renderAction(secondaryAction, false)}
            {primaryAction && renderAction(primaryAction, true)}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default ErrorScreenContent;
